<template>
  <span
    v-drag-over
    class="upload"
    :class="{ filled, invalid, uploading, compact }"
    @dragover.prevent
    @drop.prevent="onDrop"
  >
    <label class="upload-btn">
      <PortalIcon name="upload" :size="14" />
      <span v-if="uploading">Subiendo…</span>
      <span v-else><span class="upload-verb">{{ filled ? "Cambiar " : "Subir " }}</span><span class="upload-what">{{ label }}</span></span>
      <input type="file" :accept="accept" :disabled="uploading" @change="onChange" />
    </label>
    <button
      v-if="filled && removable && !uploading"
      type="button"
      class="upload-remove"
      :aria-label="'Quitar ' + label"
      :title="'Quitar ' + label"
      @click="$emit('remove')"
    >
      <PortalIcon name="x" :size="13" />
    </button>
  </span>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";

// "Subir frente" / "Cambiar frente" for one file of a document edited in place (a file dropped on it works too).
// Emits "upload" with the chosen File, and "remove".
export default {
  name: "PortalUploadButton",
  components: { PortalIcon },
  props: {
    // What it uploads, in lower case: "frente", "documento"…
    label: { type: String, required: true },
    filled: { type: Boolean, default: false },
    uploading: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    removable: { type: Boolean, default: true },
    // On a phone, where it shares a row with another one, it drops "Cambiar" once it has a file ("Frente", not
    // "Cambiar frente"): the upload icon says the rest, and the two fit side by side.
    compact: { type: Boolean, default: false },
    accept: { type: String, default: "image/*,.pdf" },
  },
  methods: {
    onChange(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.$emit("upload", file);
      e.target.value = "";
    },
    onDrop(e) {
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file && !this.uploading) this.$emit("upload", file);
    },
  },
};
</script>

<style scoped>
.upload {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  height: 34px;
  overflow: hidden;
  border: 1px dashed #cfd4de;
  border-radius: 10px;
  background: #fafbfc;
  transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
}
.upload:hover,
.upload.drag-over {
  border-color: #e6b65c;
  background: #fffbf3;
}
.upload.drag-over {
  box-shadow: 0 0 0 3px rgba(230, 182, 92, 0.18);
}
.upload.filled {
  border-style: solid;
  border-color: #e3e6ed;
  background: #fff;
}
.upload.filled:hover {
  background: #f4f5f8;
}
.upload.invalid {
  border-color: #f2c0c0;
  background: #fff6f6;
}
.upload.uploading {
  opacity: 0.7;
}
.upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-width: 0;
  gap: 7px;
  height: 100%;
  margin: 0;
  padding: 0 13px;
  font: 600 13px "Geist", system-ui, sans-serif;
  color: #0e1424;
  white-space: nowrap;
  cursor: pointer;
}
.invalid .upload-btn {
  color: #c42b2b;
}
.uploading .upload-btn {
  cursor: wait;
}
.upload-btn input {
  display: none;
}
.upload-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 100%;
  padding: 0;
  border: 0;
  border-left: 1px solid #eceef3;
  background: transparent;
  color: #8a91a3;
  cursor: pointer;
}
.upload-remove:hover {
  background: #fdecec;
  color: #c42b2b;
}

.upload-btn svg {
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .upload.compact.filled .upload-verb {
    display: none;
  }
  .upload.compact.filled .upload-what {
    text-transform: capitalize;
  }
  .upload.compact .upload-btn {
    padding: 0 8px;
    gap: 5px;
  }
  .upload.compact .upload-remove {
    width: 28px;
  }
}
/* The narrowest phones: the text and the × are enough. */
@media (max-width: 360px) {
  .upload.compact.filled .upload-btn svg {
    display: none;
  }
}
</style>
